import { Vector3 } from 'three';

import { CameraTransformation } from '../../../typings/api/skymap/rest/v0/.common';
import { CoordinateManager } from './coordinate-manager';
import { Tiled3DModelHandle } from './geodata/3d-tiled-models/tiled-3d-model-handle';
import { resolveOrthoTileOrigo } from './geodata/ortho-tile-asset/resolve-ortho-origo';
import { PointCloudHandle } from './geodata/point-clouds/point-cloud-handle';
import { ProjectGeodataManager } from './project-geodata-manager';
import { ClassicView, ViewingMode, ViewSettings } from './view-settings';

/** The scenes classic views were shown in before the SkyView scene, as shared views name them. */
export type ClassicSceneType = '3dScene' | 'orthoScene' | 'pointCloudScene';

const classicSceneTypes: Record<ClassicView, ClassicSceneType> = {
  orthophoto: 'orthoScene',
  tiledModel: '3dScene',
  pointCloud: 'pointCloudScene',
};

export const toClassicSceneType = (view: ClassicView) => classicSceneTypes[view];

export const toClassicView = (sceneType: ClassicSceneType): ClassicView =>
  (Object.keys(classicSceneTypes) as ClassicView[]).find(
    (view) => classicSceneTypes[view] === sceneType,
  )!;

function getAvailableClassicViews(): ClassicView[] {
  const available = ProjectGeodataManager.instance().resolveAvailableContainerGeodata();
  return [
    available.hasOrthoTile && ('orthophoto' as const),
    available.hasScan && ('tiledModel' as const),
    available.hasPointCloud && ('pointCloud' as const),
  ].filter((view): view is ClassicView => !!view);
}

/** The classic view if the timeline container has it, otherwise the first one it has. */
export function resolveClassicView(preferred: ClassicView): ClassicView {
  const available = getAvailableClassicViews();
  return available.includes(preferred) ? preferred : available[0] ?? 'orthophoto';
}

/**
 * Returns the classic view closest to what the flexible viewing mode shows. In perspective the
 * tiled model wins over the point cloud, as the scene then has the same origo.
 */
function resolveClosestClassicView(): ClassicView {
  const available = getAvailableClassicViews();
  if (ViewSettings.instance.camera === 'orthographic' && available.includes('orthophoto')) {
    return 'orthophoto';
  }
  return available.find((view) => view !== 'orthophoto') ?? 'orthophoto';
}

/** Returns the classic scene closest to what is shown, e.g. for a shared view. */
export function resolveClassicSceneType(): ClassicSceneType {
  const { isFlexible, classicView } = ViewSettings.instance;
  return classicSceneTypes[isFlexible ? resolveClosestClassicView() : classicView];
}

function resolveClassicSceneOrigo(sceneType: ClassicSceneType): Vector3 {
  switch (sceneType) {
    case 'orthoScene':
      return resolveOrthoTileOrigo(CoordinateManager.getInstance().getProjectProjection());
    case '3dScene':
      return Tiled3DModelHandle.instance.getOrigo();
    case 'pointCloudScene':
      return PointCloudHandle.instance.getOrigo();
  }
}

/**
 * Moves a camera transformation from the scene's local coordinates to the classic scene's, which
 * shared views are saved in.
 */
export function toClassicSceneTransformation(
  transformation: CameraTransformation,
  sceneType: ClassicSceneType,
): CameraTransformation {
  const origo = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();
  const offset = origo.clone().sub(resolveClassicSceneOrigo(sceneType));
  const { x, y, z } = transformation.position;
  return {
    ...transformation,
    position: new Vector3(x, y, z).add(offset),
  };
}

/** Moves a camera transformation from the classic scene's local coordinates to the scene's. */
export function fromClassicSceneTransformation(
  transformation: CameraTransformation,
  sceneType: ClassicSceneType,
): CameraTransformation {
  const origo = CoordinateManager.getInstance().getProjectionOrigo() ?? new Vector3();
  // The origo is the renderer's own, so it's copied before it is changed
  const offset = resolveClassicSceneOrigo(sceneType).clone().sub(origo);
  const { x, y, z } = transformation.position;
  return {
    ...transformation,
    position: new Vector3(x, y, z).add(offset),
  };
}

/**
 * Shows the viewing mode after it was the previous one, keeping the view as close as it gets: the
 * flexible mode continues the classic view with its layer, and the point cloud under the
 * orthophoto and in the tiled model, which the classic view picks from; the classic mode shows the
 * classic view closest to the flexible view.
 */
export function showViewingMode(previous: ViewingMode) {
  const settings = ViewSettings.instance;
  if (settings.viewingMode === previous) {
    return;
  }

  if (settings.isFlexible) {
    const { classicView } = settings;
    settings.showOnlyLayers(
      classicView === 'pointCloud' ? ['pointCloud'] : [classicView, 'pointCloud'],
      classicView === 'orthophoto' ? 'orthographic' : 'perspective',
    );
  } else {
    settings.showClassicView(resolveClosestClassicView());
  }
}

/** Sets the viewing mode and shows it. */
export function setViewingMode(mode: ViewingMode) {
  const previous = ViewSettings.instance.viewingMode;
  ViewSettings.instance.setViewingMode(mode);
  showViewingMode(previous);
}
